import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';

// Global event for token updates
const TOKEN_UPDATE_EVENT = 'tokens-updated';

export const useTokens = () => {
  const { user } = useAuth();
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  // Get current user
  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  };

  // Emit token update event to notify other components
  const emitTokenUpdate = () => {
    window.dispatchEvent(new CustomEvent(TOKEN_UPDATE_EVENT));
  };

  // Fetch user's current token count
  const fetchTokens = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('user_tokens')
        .select('tokens')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // If user doesn't exist, create them with initial tokens
        if (error.code === 'PGRST116') {
          await createUserTokens(user.id);
          return;
        }
        throw error;
      }

      setTokens(data.tokens);
    } catch (err) {
      console.error('Error fetching tokens:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tokens');
    } finally {
      setLoading(false);
    }
  };

  // Create new user with initial tokens (250 - 1 free CV analysis)
  const createUserTokens = async (userIdParam) => {
    const targetUserId = userIdParam || user?.id;
    if (!targetUserId) return;

    try {
      const { data, error } = await supabase
        .from('user_tokens')
        .insert([
          {
            user_id: targetUserId,
            tokens: 250, // Starting tokens (1 free CV analysis)
          }
        ])
        .select()
        .single();

      if (error) throw error;

      setTokens(data.tokens);
      emitTokenUpdate(); // Notify other components
    } catch (err) {
      console.error('Error creating user tokens:', err);
      setError(err instanceof Error ? err.message : 'Failed to create user tokens');
    }
  };

  // Consume tokens (reduce by specified amount)
  const consumeTokens = async (amount) => {
    if (!user?.id || tokens === null) {
      setError('User not authenticated or tokens not loaded');
      return false;
    }

    if (tokens < amount) {
      setError('Insufficient tokens');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const newTokenCount = tokens - amount;

      const { data, error } = await supabase
        .from('user_tokens')
        .update({ 
          tokens: newTokenCount,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setTokens(data.tokens);
      emitTokenUpdate(); // Notify other components
      return true;
    } catch (err) {
      console.error('Error consuming tokens:', err);
      setError(err instanceof Error ? err.message : 'Failed to consume tokens');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Add tokens (for purchases, bonuses, etc.)
  const addTokens = async (amount) => {
    if (!user?.id || tokens === null) {
      setError('User not authenticated or tokens not loaded');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const newTokenCount = tokens + amount;

      const { data, error } = await supabase
        .from('user_tokens')
        .update({ 
          tokens: newTokenCount,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setTokens(data.tokens);
      emitTokenUpdate(); // Notify other components
      return true;
    } catch (err) {
      console.error('Error adding tokens:', err);
      setError(err instanceof Error ? err.message : 'Failed to add tokens');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Refund tokens (specifically for refunds - fetches current state first)
  const refundTokens = async (amount) => {
    if (!user?.id) {
      setError('User not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // First, get the current token count from database to avoid state sync issues
      const { data: currentData, error: fetchError } = await supabase
        .from('user_tokens')
        .select('tokens')
        .eq('user_id', user.id)
        .single();

      if (fetchError) throw fetchError;

      const currentTokens = currentData.tokens;
      const newTokenCount = currentTokens + amount;

      const { data, error } = await supabase
        .from('user_tokens')
        .update({ 
          tokens: newTokenCount,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setTokens(data.tokens);
      emitTokenUpdate(); // Notify other components
      
      return true;
    } catch (err) {
      console.error('Error refunding tokens:', err);
      setError(err instanceof Error ? err.message : 'Failed to refund tokens');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Legacy function for backward compatibility
  const deductTokens = async (amount) => {
    return await consumeTokens(amount);
  };

  // Setup auth state listener and load tokens
  useEffect(() => {
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (session?.user?.id) {
          setUserId(session.user.id);
          fetchTokens();
        } else {
          setUserId(null);
          setTokens(null);
          setError(null);
        }
      }
    );

    // Initial load
    if (user?.id) {
      setUserId(user.id);
      fetchTokens();
    }

    // Listen for token updates from other components
    const handleTokenUpdate = () => {
      fetchTokens();
    };

    window.addEventListener(TOKEN_UPDATE_EVENT, handleTokenUpdate);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener(TOKEN_UPDATE_EVENT, handleTokenUpdate);
    };
  }, [user]);

  return {
    tokens,
    loading,
    error, 
    fetchTokens,
    consumeTokens,
    addTokens,
    refundTokens,
    createUserTokens,
    deductTokens, // Legacy function for backward compatibility
    hasEnoughTokens: (amount) => tokens !== null && tokens >= amount,
    isAuthenticated: userId !== null,
  };
};
